import { useId, useState } from "react";
import Tag from "./Tag";
import { Input } from "../catalyst/input";
import { ButtonPencil } from "./ButtonPencil";
import { ButtonAdd } from "./ButtonAdd";
import { ButtonCross } from "./ButtonCross";

export type LabelEditorOnChangeEvent = {
  action: "add" | "remove";
  item: string;
};

export default function LabelEditor({
  items,
  onSave,
  autocomplete,
  isLoading,
  onChange,
}: {
  items: string[] | undefined;
  autocomplete?: string[];
  isLoading?: boolean;
  onSave?: (tags: string[]) => Promise<void>;
  onChange?: (event: LabelEditorOnChangeEvent) => Promise<void>;
}) {
  const id = useId();
  const [isEditing, setEditingTags] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [input, setInput] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  if (!items) {
    return null;
  }

  const onRemoveClicked = (tag: string) => {
    onSaveRequested(items.filter((t) => t !== tag));
  };

  const onSaveRequested = async (data: string[]) => {
    if (onSave) {
      setIsSaving(true);

      await onSave(data)
        .then(() => {
          setInput("");
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }

    if (onChange) {
      setIsSaving(true);
      const itemsToAdd: LabelEditorOnChangeEvent[] = data
        .filter((item) => !items.includes(item))
        .map((item) => ({
          action: "add",
          item,
        }));
      const itemsToRemove: LabelEditorOnChangeEvent[] = items
        .filter((tag) => !data.includes(tag))
        ?.map((item) => ({
          action: "remove",
          item,
        }));

      for (const event of [...itemsToAdd, ...itemsToRemove]) {
        await onChange(event)
          .then(() => {
            setInput("");
          })
          .catch((err) => {
            setError(err.message);
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    }
  };

  return (
    <>
      {/* Label editing UI */}
      <div className="group">
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-1 items-center">
            {items.length === 0 && !isEditing && (
              <span className="text-gray-500 dark:text-zinc-400">—</span>
            )}

            {items.map((tag: string) => (
              <Tag
                key={tag}
                tag={tag}
                onRemove={isEditing ? () => onRemoveClicked(tag) : null}
                className={isEditing ? "" : "px-5"}
              />
            ))}

            <span className="flex items-center gap-1">
              {isEditing && (
                <>
                  <div className="w-24">
                    <Input
                      list={`available-tags-${id}`}
                      inputClassName="sm:text-xs sm:py-1"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={async (e) => {
                        const value = input.trim();
                        if (
                          e.key === "Enter" &&
                          value &&
                          !items.includes(value)
                        ) {
                          onSaveRequested([...items, value]).then(() => {
                            setEditingTags(false);
                          });
                        }
                      }}
                      placeholder="Add tag..."
                      disabled={isSaving || isLoading}
                      autoFocus
                    />
                  </div>
                  {autocomplete && (
                    <datalist id={`available-tags-${id}`}>
                      {autocomplete
                        .filter((item) => !items.includes(item))
                        .map((item) => (
                          <option key={item} value={item} />
                        ))}
                    </datalist>
                  )}
                  <ButtonCross
                    disabled={isSaving}
                    title="Cancel"
                    onClick={() => {
                      setEditingTags(false);
                      setInput("");
                    }}
                  />
                  {error && (
                    <span className="ml-2 text-xs text-red-500 dark:text-red-400">
                      {error}
                    </span>
                  )}
                </>
              )}

              {!isEditing && (
                <div className="invisible group-hover:visible">
                  {items.length > 0 ? (
                    <ButtonPencil onClick={() => setEditingTags(true)} />
                  ) : (
                    <ButtonAdd onClick={() => setEditingTags(true)} />
                  )}
                </div>
              )}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
